import React, { useState, useEffect } from "react";
import moment from "moment";
import debtImage from "../../../assets/images/debt.png";
import getFAClientName from "../../../helpers/getFAClientName";
import { Doughnut, Chart } from "react-chartjs-2";
import { chartColors } from "../../../components/form/debtPayOff/components/color";
import "../style/debtReport.css";
import { Icon } from "antd";
import {
  BalanceByCategorySkeleton,
  NumberCardSkeleton,
} from "../../Skeletons/DebtSkeleton";
export const DebtReport = ({
  strategy,
  amortizationData,
  categoryBalance,
  loading,
}) => {
  const getDateDifference = (targetDate) => {
    if (!targetDate) return "0 Months";

    const date = moment(targetDate, "MM-DD-YYYY").startOf("month");
    const today = moment().startOf("month");

    if (!date.isValid()) return "0 Months";

    // Calculate year and month difference properly
    let totalMonths = date.diff(today, "months");
    let years = Math.floor(totalMonths / 12);
    let months = totalMonths % 12;

    if (totalMonths <= 0) return "0 Months";

    if (years > 0 && months > 0) {
      return `${years} Years, ${months} Months`;
    } else if (years > 0) {
      return `${years} Years`;
    } else {
      return `${months} Months`;
    }
  };
  const [clientName, setClientName] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  let debtFreeDate =
    amortizationData && amortizationData.newDebtFree
      ? amortizationData.newDebtFree
      : null;

  let nextLoanDate =
    amortizationData &&
    amortizationData.nextDebtFree &&
    amortizationData.nextDebtFree.endingMonth
      ? amortizationData.nextDebtFree.endingMonth
      : null;
  let nextLoanName =
    amortizationData &&
    amortizationData.nextDebtFree &&
    amortizationData.nextDebtFree.nickname
      ? amortizationData.nextDebtFree.nickname
      : null;

  let diffDebtFree = "N/A";
  let diffNextLoan = getDateDifference(nextLoanDate);

  if (debtFreeDate) {
    // "April, 2025" → use "MMMM, YYYY"
    const date = moment(debtFreeDate, "MMMM, YYYY");
    const today = moment();

    const monthsDiff = date.diff(today, "months");
    const years = Math.floor(monthsDiff / 12);
    const months = monthsDiff % 12;

    diffDebtFree =
      monthsDiff > 0
        ? years > 0
          ? `${years} Years, ${months} Months`
          : `${months} Months`
        : "0 Months";
  }

  // if (nextLoanDate) {
  //   // "03-04-2027" → use "MM-DD-YYYY"
  //   const date = moment(nextLoanDate, "MM-DD-YYYY");
  //   const today = moment();

  //   const monthsDiff = date.diff(today, "months");
  //   const years = Math.floor(monthsDiff / 12);
  //   const months = monthsDiff % 12;

  //   diffNextLoan =
  //     monthsDiff > 0
  //       ? years > 0
  //         ? `${years} Years, ${months} Months`
  //         : `${months} Months`
  //       : "0 Months";
  // }

  const debtCards = [
    {
      id: 1,
      title: "Strategy",
      value: strategy ? strategy : "N/A",
    },
    {
      id: 2,
      title: "Debt Free",
      value: diffDebtFree,
      value2: debtFreeDate,
    },
    {
      id: 3,
      title: "Next Loan Paid Off",
      value: diffNextLoan,
      value2: nextLoanName,
    },
  ];

  // Group by module_name and sum balances safely
  const data =
    categoryBalance && categoryBalance.values
      ? categoryBalance.values.reduce(function (acc, item) {
          const moduleName =
            item.module_name === "Real Estates"
              ? "Real Estate"
              : item.module_name;

          const balance = parseFloat(item.balance) || 0;
          if (!acc[moduleName]) {
            acc[moduleName] = 0;
          }
          acc[moduleName] += balance;
          return acc;
        }, {})
      : {};

  // Convert grouped object into array for chart
  const uniqueData = Object.keys(data).map(function (moduleName, index) {
    return {
      color: chartColors[index],
      category: moduleName,
      amount:
        "$" +
        data[moduleName].toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      percent: "0%", // Will be updated after total amount calculation
      rawValue: data[moduleName],
    };
  });

  // Calculate total amount for percentage safely
  const totalAmount = Object.keys(data).length
    ? Object.values(data).reduce(function (sum, val) {
        return sum + val;
      }, 0)
    : 0;

  // Update percentage for each category
  uniqueData.forEach(function (d) {
    d.percent = totalAmount
      ? ((d.rawValue / totalAmount) * 100).toFixed(2) + "%"
      : "0%";
  });

  // Labels and values for chart
  const labels =
    uniqueData &&
    uniqueData.map(function (d) {
      return d.category;
    });
  const dataValues =
    uniqueData &&
    uniqueData.map(function (d) {
      return d.rawValue;
    });

  const dataDonught = {
    labels: labels || [],
    datasets: [
      {
        label: "Category Balance",
        data: dataValues || [],
        backgroundColor: chartColors.slice(
          0,
          labels && labels.length ? labels.length : 0
        ),
        borderColor: chartColors.slice(
          0,
          labels && labels.length ? labels.length : 0
        ),
        borderWidth: 1,
      },
    ],
    text: `$${(totalAmount ? totalAmount : 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`,
  };
  const centerTextPlugin = {
    id: "centerText",
    afterDraw: (chart) => {
      const { ctx } = chart;
      const text = chart.config.data.text;

      if (!text) return;

      ctx.save();

      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
      const innerRadius = chart.innerRadius || 100; // fallback in case innerRadius is missing

      // Dynamic font size based on inner radius but with limits
      let fontSize = innerRadius * 0.35; // 30% of the inner radius
      fontSize = Math.min(Math.max(fontSize, 12), 24); // keep between 12px and 24px

      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillText(text, centerX, centerY);
      ctx.restore();
    },
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const sortedData = React.useMemo(() => {
    if (!uniqueData || !uniqueData.length) return [];

    let sortable = [...uniqueData]; // make a copy of the array
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Remove $ and % for numeric sorting
        if (sortConfig.key === "amount") {
          aVal = parseFloat(aVal.replace(/[^\d.-]/g, ""));
          bVal = parseFloat(bVal.replace(/[^\d.-]/g, ""));
        } else if (sortConfig.key === "percent") {
          aVal = parseFloat(aVal.replace("%", ""));
          bVal = parseFloat(bVal.replace("%", ""));
        }

        if (sortConfig.direction === "asc") {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
    }

    return sortable;
  }, [uniqueData, sortConfig]);

  useEffect(() => {
    const name = getFAClientName();
    if (name) setClientName(name);
  }, []);

  return (
    <div className="debt-main-report">
      <div className="debt-card1">
        <h3 className="debt-report-heading">Debt Payoff Details</h3>
        <div className="debt-row">
          <div className="debt-image-and-amount-container">
            <img src={debtImage} alt="" />
            <div className="debt-amounts-container">
              {debtCards &&
                debtCards.map((item, index) => (
                  <div key={index} className="debt-report-section">
                    <div
                      className="debt-heading-container"
                      style={{ fontWeight: "700" }}
                    >
                      {item.title}
                    </div>
                    <div
                      className="debt-report-amount-section"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <div>{loading ? <NumberCardSkeleton /> : item.value}</div>
                      {item.value2 && (
                        <div>
                          {item.value2 && loading ? (
                            <NumberCardSkeleton />
                          ) : (
                            item.value2
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <div>
            <div className="reportrow">
              <div className="reportrowData">
                <span className="reportTitle"> Client Name</span>
                <span className="reportsubTitle">
                  {clientName || localStorage.getItem("User") || ""}
                </span>
              </div>
              <div className="reportrowData">
                <span className="reportTitle"> Today's Date </span>
                <span className="reportsubTitle">
                  {" "}
                  {moment().format("MM/DD/YYYY")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="debt-card2" style={{ position: "relative" }}>
        <h1 className="debt-card-heading">Balance By Category</h1>
        {loading ? (
          <BalanceByCategorySkeleton />
        ) : (
          <div className="debt-balance-container" style={{ flexWrap: "wrap" }}>
            <div className="debt-balance-table-container">
              <div className="table-wrapper">
                <table className="debt-table">
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSort("category")}
                        style={{ cursor: "pointer" }}
                      >
                        Category{" "}
                        <Icon
                          type={
                            sortConfig.key === "category" &&
                            sortConfig.direction === "asc"
                              ? "caret-up"
                              : "caret-down"
                          }
                        />
                      </th>
                      <th
                        onClick={() => handleSort("amount")}
                        style={{ cursor: "pointer" }}
                      >
                        Amount
                        <Icon
                          type={
                            sortConfig.key === "amount" &&
                            sortConfig.direction === "asc"
                              ? "caret-up"
                              : "caret-down"
                          }
                        />
                      </th>
                      <th
                        onClick={() => handleSort("percent")}
                        style={{ cursor: "pointer" }}
                      >
                        %{" "}
                        <Icon
                          type={
                            sortConfig.key === "percent" &&
                            sortConfig.direction === "asc"
                              ? "caret-up"
                              : "caret-down"
                          }
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData &&
                      sortedData.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <span
                              className={`dot ${row.color}`}
                              style={{ background: row.color }}
                            ></span>
                            <span className="category">{row.category}</span>
                          </td>
                          <td className="amount">{row.amount}</td>
                          <td>{row.percent}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="debt-balance-chart-container">
              <Doughnut
                data={dataDonught}
                width={300}
                options={{
                  cutoutPercentage: 70,
                  // responsive:true,
                  legend: {
                    display: false,
                    position: "right",
                  },
                  tooltips: {
                    callbacks: {
                      label: function (tooltipItem, data) {
                        const dataset = data.datasets[tooltipItem.datasetIndex];
                        const datasetLabel = dataset.label || "";
                        const value = dataset.data[tooltipItem.index] || 0;
                        const formattedValue = parseFloat(value)
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                        return `${datasetLabel}: $${formattedValue}`;
                      },
                    },
                  },
                }}
                plugins={[centerTextPlugin]}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
